/**
 * Reusable focus trap for modal/overlay elements.
 *
 * Manages:
 * - inert attribute on background content (prevents focus escaping)
 * - Tab/Shift+Tab cycling within the container
 * - Optional Escape key handling
 * - Return focus to trigger element on deactivate
 */

var FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
].join(', ');

/**
 * @param {HTMLElement} container - The modal/overlay element
 * @param {Object} [options]
 * @param {Function} [options.onClose] - Called when Escape is pressed
 * @param {boolean} [options.escapeCloses] - Whether Escape triggers onClose (default: true)
 * @returns {{ activate: Function, deactivate: Function }}
 */
export function createFocusTrap(container, options) {
    var onClose = options && options.onClose || null;
    var escapeCloses = options && options.escapeCloses !== undefined ? options.escapeCloses : true;
    var previouslyFocused = null;

    function getFocusableElements() {
        var all = container.querySelectorAll(FOCUSABLE_SELECTOR);
        var result = [];
        for (var i = 0; i < all.length; i++) {
            if (!all[i].closest('[inert]')) {
                result.push(all[i]);
            }
        }
        return result;
    }

    function handleKeydown(e) {
        if (e.key === 'Escape' && escapeCloses && onClose) {
            e.preventDefault();
            onClose();
            return;
        }

        if (e.key !== 'Tab') return;

        var focusable = getFocusableElements();
        if (focusable.length === 0) {
            e.preventDefault();
            return;
        }

        var first = focusable[0];
        var last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    }

    function findBodyAncestor(el) {
        var current = el;
        while (current.parentElement && current.parentElement !== document.body) {
            current = current.parentElement;
        }
        return current;
    }

    function setBackgroundInert(value) {
        var root = findBodyAncestor(container);
        var children = document.body.children;
        for (var i = 0; i < children.length; i++) {
            var child = children[i];
            if (child !== root && child.nodeType === 1 && child.tagName !== 'SCRIPT' && child.tagName !== 'STYLE' && child.tagName !== 'LINK') {
                if (value) {
                    child.setAttribute('inert', '');
                } else {
                    child.removeAttribute('inert');
                }
            }
        }
    }

    function activate(triggerElement) {
        previouslyFocused = triggerElement || document.activeElement;
        setBackgroundInert(true);
        container.addEventListener('keydown', handleKeydown);
    }

    function deactivate() {
        container.removeEventListener('keydown', handleKeydown);
        setBackgroundInert(false);
        if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
            previouslyFocused.focus();
        }
        previouslyFocused = null;
    }

    return { activate: activate, deactivate: deactivate };
}
